<template>
  <div class="event-market-container">
    <div class="edit-form-title">
      <h1>Market Size</h1>
      <div class="package-header-note">
        No one knows your audience better than you.
        Include size and demographic information about your event attendees.
        The more detailed information you provide,
        the more potential sponsors will understand who they can access.
      </div>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>Audience Size
      </label>
        <v-select :options="allAudienceSizes"
          placeholder="Choose Audience Size..."
          v-model="audienceSize" class="audience-size-select">
        </v-select>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>Age Group
        <tooltip>
          Remember to demonstrate different target groups. How many people your event
          draws and align with the target market.
        </tooltip>
      </label>
      <div v-for="(ageGroup, i) in ageGroups" :key="i" class="age-group-container">
        <v-select :options="allAgeGroups"
          placeholder="Choose Age Group..."
          v-model="ageGroups[i].title" class="age-group-select">
        </v-select>
        <input type="number" v-model="ageGroups[i].percentage" class="age-group-percent"
          placeholder="%"/>
        <font-awesome-icon :icon="['fa', 'minus-square']" size="1x"
          class="delete-age-group" @click="ageGroups.splice(i, 1)">
        </font-awesome-icon>
      </div>
      <button class="add-link-button" aria-label="Add Age Group"
        @click="addAgeGroup()">
        + Age Group
      </button>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>Gender Distribution
      </label>
      <div class="gender-distribution-container">
        <span>Male</span>
        <span>({{genderDistribution}}%)</span>
        <input type="range" min="0" max="100" v-model="genderDistribution"/>
        <span>Female</span>
        <span>({{100 - genderDistribution}}%)</span>
      </div>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>Market Reach
        <tooltip>
          Market reach demonstrate the current scope  of your event
        </tooltip>
      </label>
      <v-select :options="allMarketReaches"
        placeholder="Choose a market reach..."
        v-model="marketReach" class="market-reach-select">
      </v-select>
    </div>
    <!--div class="edit-form edit-form-margin-top">
    <label>Google Analytics</label>
    <span> Enter your tracking ID to enable Google Analytics on your Event.
    </span>
    <input class="atomic-text-input" id="google-analytics"
      placeholder="UA-000000-0" type="text" value="">
    </div-->
    <!-- COMMENTED OUT THE METRICS FEATURE ON FRONT END FOR NOW -->
    <div class="edit-form edit-form-margin-top" v-if="false">
      <label>Event Metrics</label>
      <!--span> What are your key metrics? Metrics worth getting sponsorship.
      </span-->
      <div v-for="(metric, i) in metrics" :key="i" class ="metrics-data">
        <input class="metric-text-input"
          placeholder="Ex. Ticket Sold" type="text" v-model="metrics[i].title">
        <select v-model="metrics[i].month" class="metric-text-input metric-select">
          <option :value="undefined" disabled>Month</option>
          <option v-for="(month, j) in months" :key="j"
            :value="month.mm">
            {{month.label}}
          </option>
        </select>
        <select v-model="metrics[i].year" class="metric-text-input metric-select">
          <option :value="undefined" disabled>Year</option>
          <option v-for="(year, j) in years" :key="j"
            :value="year">
            {{year}}
          </option>
        </select>
        <input class="metric-text-input"
          placeholder="Value" type="text" v-model="metrics[i].value">
        <select class="metric-text-input"
          v-model="metrics[i].period">
          <option :value="undefined" disabled>Period</option>
          <option v-for="(metricPeriod, i) in metricPeriods"
            :key="i" :value="metricPeriod">
            {{metricPeriod}}
          </option>
        </select>
        <font-awesome-icon :icon="['fa', 'minus-square']" size="1x"
          class="delete-metric" @click="metrics.splice(i, 1)">
        </font-awesome-icon>
      </div>
      <button class="add-link-button" aria-label="Add Metric"
        @click="addMetric()">
        +Metric
      </button>
    </div>
    <div class="edit-form">
      <button class="button" aria-label="Next" @click="goNext">Next</button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';

export default {
  name: 'event-market-trend',
  data() {
    return {
      allAgeGroups: [],
      allAudienceSizes: [],
      metricPeriods: [],
      ageGroups: [],
      audienceSize: '',
      metrics: [],
      genderDistribution: 50,
      allMarketReaches: [],
      marketReach: '',
    };
  },
  computed: {
    ...mapState('auth', ['backendAxios']),
    ...mapState('utils', ['months', 'years']),
    autoSaveProps() {
      return () => [
        this.allAgeGroups,
        this.allAudienceSizes,
        this.metricPeriods,
        this.ageGroups,
        this.audienceSize,
        this.metrics,
        this.genderDistribution,
        this.allMarketReaches,
        this.marketReach,
      ].join('');
    },
  },
  created() {
    this.debouncedSave = debounce(this.save, 2000);
  },
  async mounted() {
    try {
      const ageGroupsResponse =
        await this.backendAxios.get('/sponsorship-event/age-groups');
      this.allAgeGroups = ageGroupsResponse.data;

      const audienceSizesResponse =
        await this.backendAxios.get('/sponsorship-event/audience-sizes');
      this.allAudienceSizes = audienceSizesResponse.data;

      const metricPeriodsResponse =
        await this.backendAxios.get('/sponsorship-event/metric-periods');
      this.metricPeriods = metricPeriodsResponse.data;

      const marketReachesResponse =
        await this.backendAxios.get('/sponsorship-event/market-reaches');
      this.allMarketReaches = marketReachesResponse.data;

      this.eventId = this.$route.params.eventId;
      const fields = ['audienceSize', 'ageGroups', 'metrics', 'genderDistribution', 'marketReach'];
      const eventResponse = await this.backendAxios
        .get(`/sponsorship-event/my-event/id/${this.eventId}?fields=${fields.join(',')}`);
      const {
        ageGroups, audienceSize, metrics, genderDistribution, marketReach,
      } = eventResponse.data;
      this.ageGroups = ageGroups || [];
      this.marketReach = marketReach || '';
      this.audienceSize = audienceSize || '';
      this.metrics = metrics || [];
      this.genderDistribution = genderDistribution || 50;
      if (this.ageGroups.length === 0) {
        // add an empty age group item if none present
        this.addAgeGroup();
      }
      const unwatchAutoSaveProps = this.$watch(this.autoSaveProps, () => {
        this.debouncedSave();
      });
      this.$once('hook:destroyed', () => {
        unwatchAutoSaveProps();
      });
    } catch (err) {
      this.$toasted.show(err.response.data, {
        type: 'info',
        duration: 5000,
      });
    }
  },
  methods: {
    addAgeGroup() {
      this.ageGroups.push({
        title: '',
        percentage: '',
      });
    },
    addMetric(data = {}) {
      const metric = {};
      metric.title = data.title;
      metric.month = data.month;
      metric.year = data.year;
      metric.value = data.value;
      metric.period = data.period;
      this.metrics.push(metric);
    },
    async goNext() {
      this.debouncedSave.cancel();
      if (await this.save()) {
        // this.$router.push(`/event/${this.eventId}/edit/team`);
        this.$emit('next', this.$route.name);
      }
    },
    async save() {
      try {
        const params = {
          audienceSize: this.audienceSize,
          ageGroups: this.ageGroups,
          marketReach: this.marketReach,
          metrics: this.metrics,
          genderDistribution: this.genderDistribution || 0,
        };
        await this.backendAxios
          .put(`/sponsorship-event/my-event/id/${this.eventId}`, params);
        return true;
      } catch (err) {
        this.$toasted.show(err.response.data, {
          type: 'info',
          duration: 5000,
        });
      }
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/youpaired-colors.scss';

.audience-size-select, .market-reach-select {
  width: 100%;
  background-color: white;
  padding: 0 0 1rem 0;
}

.age-group-container {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 0.5rem;
}

.age-group-select {
  width: 70%;
  background-color: white;
}

.age-group-percent {
  width: 25%;
  margin-left: 1rem;
  height: 2.4rem;
  border-radius: 0.25rem;
}

.metric-text-input {
  text-align: left;
  font-size: 0.875rem;
  font-weight: normal;
  color: #484848;
  background-color: white;
  height: 2.5em;
  border: 1px solid #DCDBDB;
  border-radius: 0.25rem;
  margin: 0.15rem;
}

.metrics-data {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
}

@media screen and (max-width: 320px) {
  .metric-select  {
    width: 3rem;
  }
}

.delete-metric, .delete-age-group {
  color: rgb(214, 14, 14);
  cursor: pointer;
  margin-left: 0.35rem;
}

.gender-distribution-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.gender-distribution-container input[type="range"] {
  margin: 0 1rem;
}

.gender-distribution-container div {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

</style>
